import React, { FC, useCallback, useMemo } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';

import {
  getBadgeLabel,
  getBadgePlaceholder,
  shouldRenderBadge
} from 'views/Offers/Offer.utils';

import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import RichText from 'components/RichText/RichText';
import {
  FormControl,
  FormField,
  FormItem
} from 'components/ui';

import { IBadgesSectionProps } from './sections.types';

const NONE_OPTION = { value: '', label: 'None' };

export const BadgesSection: FC<IBadgesSectionProps> = ({
  relevantBadgeType,
  badgeList
}): React.ReactElement | null => {
  const { control, setValue } = useFormContext();
  const currentBadge = useWatch({ control, name: 'badge' });

  const handleBadgeChange = useCallback(
    (value: string): void => {
      const resolved = value === '' ? null : value;
      setValue('badge', resolved, {
        shouldDirty: true,
        shouldValidate: true
      });
    },
    [setValue]
  );

  const badgeOptions = useMemo(
    () => [NONE_OPTION, ...badgeList],
    [badgeList]
  );

  const selectedOption = useMemo(
    () => badgeList.find((item) => item.value === currentBadge),
    [badgeList, currentBadge]
  );

  const defaultSelected = useMemo(() => {
    if (!selectedOption) {
      return undefined;
    }
    return { value: selectedOption.value, label: selectedOption.label };
  }, [selectedOption]);

  const label = useMemo(
    () => getBadgeLabel(relevantBadgeType),
    [relevantBadgeType]
  );

  const placeholder = useMemo(
    () => getBadgePlaceholder(relevantBadgeType),
    [relevantBadgeType]
  );

  const renderBadgeField = useCallback(
    (): React.ReactElement => (
      <FormItem>
        <FormControl>
          <InputSelectDropdown
            label={label}
            placeholder={placeholder}
            buttonDataTestId="promoBadgesInpt"
            options={badgeOptions}
            defaultSelected={defaultSelected}
            onOptionChange={handleBadgeChange}
          />
        </FormControl>
      </FormItem>
    ),
    [label, placeholder, badgeOptions, defaultSelected, handleBadgeChange]
  );

  if (!shouldRenderBadge(relevantBadgeType)) {
    return null;
  }

  return (
    <React.Fragment>
      <RichText
        className="mb-5"
        title="Badges"
        description='Highlight your offer with a badge like "Most Popular" or "Best Value"'
      />
      <div className="grid grid-cols-2 gap-5">
        <FormField
          control={control}
          name="badge"
          render={renderBadgeField}
        />
      </div>
    </React.Fragment>
  );
};
